'use client';

import { useEffect, useState } from 'react';
import { getAuthClient } from '@/lib/auth-client';
import { resetAuthSession } from '@/lib/auth-reset';
import { logger } from '@/lib/logger';

// gh #1009: how long the lock overlay may sit waiting for a manual "Resume
// Session" click before we stop waiting and force a hard redirect to /login.
// The session is already locked server-side by lockSession() (bearer
// blocklist + cookie clear + force-prompt flag), so this is a UX backstop —
// not a security control — that stops a fully-expired session from leaving a
// dead screen behind the overlay. Matches the CJIS 5.6.3.1 idle window (30m).
const SESSION_LOCK_MAX_DWELL_MS = 30 * 60 * 1000;

interface SessionLockOverlayProps {
  onResume: () => void;
  // Overridable for tests / tuning; defaults to SESSION_LOCK_MAX_DWELL_MS.
  maxDwellMs?: number;
}

export function SessionLockOverlay({
  onResume,
  maxDwellMs = SESSION_LOCK_MAX_DWELL_MS,
}: SessionLockOverlayProps) {
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // gh #1009: bounded max-dwell backstop. Without this the overlay waits
  // forever for a manual "Resume Session" click, and app/providers.tsx
  // suppresses its onAuthStateChanged redirect while locked
  // (deliberateOrIdleLock === true). So a session that fully expires behind
  // the overlay — the 12h IA-11 absolute cap, an IdP session end, or simply a
  // user who walked away from the lock screen — leaves a dead page that only a
  // manual click recovers (the original complaint). Arm a timer for the
  // overlay's lifetime; on expiry force resetAuthSession('session-expired'),
  // which clears the __session cookie + caches and navigates to /login. Its
  // gh #946 freshness guard suppresses the redirect if a session was just
  // (re)established, so a tab that re-authenticated will not bounce. Each
  // locked tab (the overlay is raised cross-tab via the session-lock
  // BroadcastChannel in providers.tsx) runs its own timer and redirects
  // independently, so there is no cross-tab race.
  useEffect(() => {
    const dwellTimer = setTimeout(() => {
      logger.warn(
        '[SessionLockOverlay] Max dwell reached on lock screen — redirecting to login'
      );
      void resetAuthSession('session-expired');
    }, maxDwellMs);
    return () => clearTimeout(dwellTimer);
  }, [maxDwellMs]);

  const handleResume = async () => {
    let shouldResetResuming = true;
    setResuming(true);
    setError(null);

    try {
      // Full interactive re-auth. lockSession() armed the force-prompt-login
      // flag, so signIn() does signinRedirect with prompt=login + login_hint.
      // Silent renew is not an option: the prior bearer is blocklisted and
      // the session cookie is cleared. CJIS AC-11 / SC-10 session unlock
      // requires re-authentication, not a UI dismiss. The thrown
      // "Redirect in progress" is expected (flow leaves this page).
      const client = getAuthClient();
      await client.signIn('', '');
      // Only reached if signIn somehow returned without redirecting.
      onResume();
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'Redirect in progress') {
        // Expected path; stay in "Resuming" state until navigation commits.
        shouldResetResuming = false;
        return;
      }
      shouldResetResuming = false;
      setError('Session expired. Redirecting to login...');
      setTimeout(() => {
        // Session is gone; a hard reload discards the locked app state
        // rather than carrying it into the login page.
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- hard reload after session expiry
        window.location.href = '/login';
      }, 1500);
    } finally {
      if (shouldResetResuming) {
        setResuming(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-8 shadow-2xl">
        <div className="mb-2 text-center text-4xl">🔒</div>
        <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">
          Session Locked
        </h2>
        <p className="mb-6 text-center text-sm text-gray-600">
          Your session was locked due to inactivity.
        </p>

        {error && (
          <p className="mb-4 text-center text-sm text-red-600">{error}</p>
        )}

        <button
          onClick={handleResume}
          disabled={resuming}
          className="w-full rounded-md bg-orange-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
        >
          {resuming ? 'Resuming...' : 'Resume Session'}
        </button>
      </div>
    </div>
  );
}
